"use client";

import { useEffect, useState } from "react";
import { FileText } from "lucide-react";

import { MarkdownFromText } from "@/components/assistant-ui/markdown-text";
import { GenerateDocumentationControls } from "@/components/generate-documentation-controls";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import type { DocumentationTemplateKind } from "@/lib/documentation/generate-documentation";
import { cn } from "@/lib/utils";

export interface DocumentationMarkdownFieldProps {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly isLoading?: boolean;
  readonly placeholder?: string;
  readonly hideLabel?: boolean;
  readonly defaultTab?: "write" | "preview";
  /** Taller write/preview panes for the half-page documentation flyout. */
  readonly tall?: boolean;
  /** Stretch write/preview panes to fill the parent flex column (task detail). */
  readonly fillHeight?: boolean;
  /** Section title shown on the same row as Write / Preview (e.g. "Documentation"). */
  readonly heading?: string;
}

export function DocumentationMarkdownField({
  id = "documentation",
  value,
  onChange,
  disabled = false,
  isLoading = false,
  placeholder = "Write markdown. Use headings for PRD, PRFAQ, and other agent context.",
  hideLabel = false,
  defaultTab = "write",
  tall = false,
  fillHeight = false,
  heading,
}: DocumentationMarkdownFieldProps): JSX.Element {
  const paneMinHeight = fillHeight
    ? "min-h-0 flex-1"
    : tall
      ? "min-h-[min(85vh,52rem)]"
      : "min-h-64";

  const fillLayout = fillHeight;

  return (
    <div
      className={cn(
        "space-y-2",
        fillLayout && "flex h-full min-h-0 flex-1 flex-col",
      )}
    >
      <Tabs
        defaultValue={defaultTab}
        key={defaultTab}
        className={cn(fillLayout && "flex h-full min-h-0 flex-1 flex-col")}
      >
        <div className="flex shrink-0 items-center justify-between gap-3">
          {heading ? (
            <h2 className="min-w-0 truncate text-lg font-medium">{heading}</h2>
          ) : hideLabel ? (
            <div />
          ) : (
            <div className="space-y-1">
              <Label htmlFor={id}>Documentation</Label>
              <p className="text-xs text-muted-foreground">
                Markdown. Saved only when you click Save.
              </p>
            </div>
          )}
          <TabsList>
            <TabsTrigger value="write">Write</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent
          value="write"
          className={cn(
            fillLayout &&
              "mt-2 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden",
          )}
        >
          {isLoading ? (
            <Skeleton className={cn("w-full", paneMinHeight)} />
          ) : (
            <Textarea
              id={id}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              disabled={disabled}
              placeholder={placeholder}
              className={cn(
                "font-mono text-sm leading-6",
                fillLayout
                  ? "min-h-0 flex-1 resize-none overflow-y-auto"
                  : cn("resize-y", paneMinHeight),
              )}
            />
          )}
        </TabsContent>
        <TabsContent
          value="preview"
          className={cn(
            fillLayout &&
              "mt-2 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden",
          )}
        >
          <div
            className={cn(
              "overflow-y-auto rounded-md border border-input bg-background px-4 py-3",
              paneMinHeight,
            )}
          >
            <DocumentationPreview
              isLoading={isLoading}
              value={value}
              tall={tall || fillHeight}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DocumentationPreview({
  isLoading,
  value,
  tall = false,
}: {
  readonly isLoading: boolean;
  readonly value: string;
  readonly tall?: boolean;
}): JSX.Element {
  if (isLoading) {
    return <Skeleton className={tall ? "h-64 w-full" : "h-48 w-full"} />;
  }
  if (value.trim()) {
    return (
      <div
        className={
          tall ? "prose prose-sm dark:prose-invert max-w-none" : undefined
        }
      >
        <MarkdownFromText text={value} />
      </div>
    );
  }
  return <p className="text-sm text-muted-foreground">Nothing to preview.</p>;
}

export interface DocumentationEditorFlyoutProps {
  readonly documentation: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly isLoading?: boolean;
  /** When false (parent sidebar closed), forces the flyout closed. */
  readonly active?: boolean;
  /** Persist generated docs immediately (e.g. update facet). */
  readonly persistDocumentation?: (documentation: string) => Promise<void>;
  readonly generate?: {
    readonly targetKind: "facet" | "work";
    readonly targetId: string;
    readonly templateKind: DocumentationTemplateKind | null;
    readonly name: string;
    readonly description: string;
  } | null;
}

interface DocumentationEditorBodyProps {
  readonly documentation: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly isLoading?: boolean;
  readonly persistDocumentation?: (documentation: string) => Promise<void>;
  readonly generate?: DocumentationEditorFlyoutProps["generate"];
  readonly fieldId?: string;
  readonly tall?: boolean;
  readonly fillHeight?: boolean;
  /** Shown beside Write / Preview (inline card + flyout). */
  readonly heading?: string;
}

function DocumentationEditorBody({
  documentation,
  onChange,
  disabled = false,
  isLoading = false,
  persistDocumentation,
  generate = null,
  fieldId = "documentation-editor",
  tall = false,
  fillHeight = false,
  heading,
}: DocumentationEditorBodyProps): JSX.Element {
  const hasDoc = Boolean(documentation.trim());
  const canGenerate = Boolean(generate?.targetId);

  const handleGenerated = async (next: string): Promise<void> => {
    onChange(next);
    if (persistDocumentation) {
      await persistDocumentation(next);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        fillHeight && "h-full min-h-0 flex-1",
      )}
    >
      {canGenerate && generate ? (
        <div className="shrink-0">
          <GenerateDocumentationControls
            targetKind={generate.targetKind}
            targetId={generate.targetId}
            templateKind={generate.templateKind}
            name={generate.name}
            description={generate.description}
            documentation={documentation}
            disabled={disabled || isLoading}
            buttonLabel={hasDoc ? "Regenerate with AI" : "Generate with AI"}
            onApplied={handleGenerated}
            savesOnApply={Boolean(persistDocumentation)}
          />
        </div>
      ) : null}

      {hasDoc || !canGenerate ? (
        <DocumentationMarkdownField
          id={fieldId}
          value={documentation}
          onChange={onChange}
          disabled={disabled}
          isLoading={isLoading}
          hideLabel={!heading}
          heading={heading}
          tall={tall}
          fillHeight={fillHeight}
          defaultTab={hasDoc ? "preview" : "write"}
        />
      ) : (
        <div
          className={cn("flex flex-col gap-3", fillHeight && "min-h-0 flex-1")}
        >
          {heading ? <h2 className="text-lg font-medium">{heading}</h2> : null}
          <p className="text-sm text-muted-foreground">
            No documentation yet. Generate with AI.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Inline documentation editor for two-column work detail layouts.
 */
export function DocumentationInlinePanel({
  documentation,
  onChange,
  disabled = false,
  isLoading = false,
  persistDocumentation,
  generate = null,
}: DocumentationEditorFlyoutProps): JSX.Element {
  return (
    <DocumentationEditorBody
      documentation={documentation}
      onChange={onChange}
      disabled={disabled}
      isLoading={isLoading}
      persistDocumentation={persistDocumentation}
      generate={generate}
      fieldId="documentation-inline"
      fillHeight
      heading="Documentation"
    />
  );
}

/**
 * "View Documentation" trigger for the sidebar header + nested flyout.
 * Existing docs → preview/edit. Empty → Generate with AI.
 */
export function DocumentationEditorFlyout({
  documentation,
  onChange,
  disabled = false,
  isLoading = false,
  active = true,
  persistDocumentation,
  generate = null,
}: DocumentationEditorFlyoutProps): JSX.Element {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!active) {
      setOpen(false);
    }
  }, [active]);

  const heading = generate?.name?.trim()
    ? `${generate.name.trim()} Documentation`
    : "Documentation";

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <FileText className="size-3.5 shrink-0" aria-hidden />
        View Documentation
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="z-[60] flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden sm:!w-1/2 sm:!max-w-[50vw]"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{heading}</SheetTitle>
          </SheetHeader>

          <DocumentationEditorBody
            documentation={documentation}
            onChange={onChange}
            disabled={disabled}
            isLoading={isLoading}
            persistDocumentation={persistDocumentation}
            generate={generate}
            fieldId="documentation-flyout"
            fillHeight
            heading={heading}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
